import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/store';
import {
  Clock, AlertTriangle, Calendar, TrendingUp, Search,
  Plus, Download, Edit2, Trash2, Check, X, Loader2,
  MessageCircle, Send, Filter, Bell, BellOff, Settings,
  RotateCcw, Archive, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (val) => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR'); } catch { return '-'; }
};

const daysDiff = (dateStr) => {
  if (!dateStr) return 0;
  const now = new Date();
  const due = new Date(dateStr);
  return Math.floor((now - due) / (1000 * 60 * 60 * 24));
};

export default function AdminPaymentTrackingPage() {
  const { token } = useAuthStore();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending'); // all, pending, overdue, today, paid
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [reminderSettings, setReminderSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingReminder, setTogglingReminder] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedPayments, setDeletedPayments] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [restoring, setRestoring] = useState(null);

  // Dealer selection state
  const [dealers, setDealers] = useState([]);
  const [dealerSearch, setDealerSearch] = useState('');
  const [showDealerDropdown, setShowDealerDropdown] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);

  // WhatsApp connection status
  const [waStatus, setWaStatus] = useState({ status: 'disconnected' });

  useEffect(() => {
    const checkWA = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/whatsapp/status`, { headers: { Authorization: `Bearer ${token}` } });
        setWaStatus(res.data || { status: 'disconnected' });
      } catch { setWaStatus({ status: 'disconnected' }); }
    };
    checkWA();
    const interval = setInterval(checkWA, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [token]);

  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    amount: '',
    due_date: '',
    description: '',
    dealer_id: '',
    send_whatsapp: true,
  });

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/payment-tracking`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const fetchDeletedPayments = useCallback(async () => {
    setLoadingDeleted(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/payment-tracking/deleted`, { headers: { Authorization: `Bearer ${token}` } });
      setDeletedPayments(res.data || []);
    } catch {}
    finally { setLoadingDeleted(false); }
  }, [token]);

  const handleRestore = async (id) => {
    setRestoring(id);
    try {
      await axios.put(`${API_URL}/api/admin/payment-tracking/${id}/restore`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Geri yüklendi');
      fetchDeletedPayments();
      fetchPayments();
    } catch { toast.error('Geri yükleme başarısız'); }
    finally { setRestoring(null); }
  };

  useEffect(() => {
    const fetchDealers = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/dealers?limit=1000`, { headers: { Authorization: `Bearer ${token}` } });
        setDealers(res.data.dealers || []);
      } catch {}
    };
    fetchDealers();
  }, [token]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/payment-tracking/reminder-settings`, { headers: { Authorization: `Bearer ${token}` } });
        setReminderSettings(res.data);
      } catch { setReminderSettings({ enabled: true, days_before_due: 3, overdue_reminder_interval: 7, reminder_hour: 10 }); }
    };
    fetchSettings();
  }, [token]);

  const saveReminderSettings = async () => {
    if (!reminderSettings) return;
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/admin/payment-tracking/reminder-settings`, reminderSettings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Hatırlatma ayarları kaydedildi');
      setShowSettings(false);
    } catch { toast.error('Ayarlar kaydedilemedi'); }
    finally { setSavingSettings(false); }
  };

  const toggleAutoReminder = async (p) => {
    setTogglingReminder(p.id);
    try {
      const newState = !(p.auto_reminder !== false);
      await axios.put(`${API_URL}/api/admin/payment-tracking/${p.id}/auto-reminder`, { auto_reminder: newState }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newState ? 'Otomatik hatirlatma acildi' : 'Otomatik hatirlatma kapatildi');
      fetchPayments();
    } catch { toast.error('İşlem başarısız'); }
    finally { setTogglingReminder(null); }
  };

  // Stats
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];

  const pending = payments.filter(p => p.status !== 'paid');
  const overdue = pending.filter(p => p.due_date && new Date(p.due_date) < now);
  const dueToday = pending.filter(p => p.due_date && p.due_date.startsWith(todayStr));
  const paid = payments.filter(p => p.status === 'paid');
  const msgSent = payments.filter(p => p.last_reminder_sent_at).length;

  const pendingTotal = pending.reduce((s, p) => s + (p.amount || 0), 0);
  const overdueTotal = overdue.reduce((s, p) => s + (p.amount || 0), 0);
  const todayTotal = dueToday.reduce((s, p) => s + (p.amount || 0), 0);
  const paidTotal = paid.reduce((s, p) => s + (p.amount || 0), 0);

  // Filtered list
  let filtered = payments;
  if (filter === 'pending') filtered = pending;
  else if (filter === 'overdue') filtered = overdue;
  else if (filter === 'today') filtered = dueToday;
  else if (filter === 'paid') filtered = paid;

  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(p =>
      (p.customer_name || '').toLowerCase().includes(q) ||
      (p.customer_phone || '').includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  }

  const openAddModal = () => {
    setEditingPayment(null);
    setForm({ customer_name: '', customer_phone: '', amount: '', due_date: '', description: '', dealer_id: '', send_whatsapp: true });
    setSelectedDealer(null);
    setDealerSearch('');
    setShowDealerDropdown(false);
    setShowModal(true);
  };

  const openEditModal = (p) => {
    setEditingPayment(p);
    setForm({
      customer_name: p.customer_name || '',
      customer_phone: p.customer_phone || '',
      amount: p.amount?.toString() || '',
      due_date: p.due_date ? p.due_date.split('T')[0] : '',
      description: p.description || '',
      dealer_id: p.dealer_id || '',
      send_whatsapp: false,
    });
    if (p.dealer_id) {
      const d = dealers.find(x => x.id === p.dealer_id);
      setSelectedDealer(d || null);
    } else {
      setSelectedDealer(null);
    }
    setDealerSearch('');
    setShowDealerDropdown(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.amount || !form.due_date) {
      toast.error('İsim, tutar ve vade tarihi zorunludur');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        amount: parseFloat(form.amount),
        due_date: form.due_date,
        description: form.description || null,
        dealer_id: form.dealer_id || null,
        send_whatsapp: form.send_whatsapp,
      };
      if (editingPayment) {
        await axios.put(`${API_URL}/api/admin/payment-tracking/${editingPayment.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ödeme sözü güncellendi');
      } else {
        await axios.post(`${API_URL}/api/admin/payment-tracking`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Ödeme sözü eklendi');
      }
      setShowModal(false);
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`"${p.customer_name}" icin odeme sozunu silmek istediginize emin misiniz?`)) return;
    setDeleting(p.id);
    try {
      await axios.delete(`${API_URL}/api/admin/payment-tracking/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Silindi');
      fetchPayments();
    } catch { toast.error('Silinemedi'); }
    finally { setDeleting(null); }
  };

  const handleMarkPaid = async (p) => {
    setMarkingPaid(p.id);
    try {
      await axios.post(`${API_URL}/api/admin/payment-tracking/${p.id}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tahsil edildi olarak işaretlendi');
      fetchPayments();
    } catch { toast.error('İşlem başarısız'); }
    finally { setMarkingPaid(null); }
  };

  const handleUnmarkPaid = async (p) => {
    try {
      await axios.post(`${API_URL}/api/admin/payment-tracking/${p.id}/unmark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ödeme durumu geri alındı');
      fetchPayments();
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleSendReminder = async (p) => {
    try {
      await axios.post(`${API_URL}/api/admin/payment-tracking/${p.id}/send-reminder`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Hatırlatma mesajı gönderildi');
      fetchPayments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Mesaj gönderilemedi'); }
  };

  const getStatusBadge = (p) => {
    if (p.status === 'paid') return { label: 'Tahsil Edildi', color: 'bg-green-100 text-green-800' };
    if (p.due_date && new Date(p.due_date) < now) {
      const days = daysDiff(p.due_date);
      return { label: `${days} gün gecikti`, color: 'bg-red-100 text-red-800' };
    }
    if (p.due_date && p.due_date.startsWith(todayStr)) return { label: 'Bugün Vadeli', color: 'bg-blue-100 text-blue-800' };
    if (p.due_date) {
      const days = -daysDiff(p.due_date);  // positive = days remaining
      const color = days <= 3 ? 'bg-orange-100 text-orange-800' : days <= 7 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700';
      return { label: `${days} gün kaldı`, color };
    }
    return { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800' };
  };

  const stats = [
    { icon: Clock, color: 'text-yellow-500 bg-yellow-50', label: 'Bekleyen Toplam', amount: pendingTotal, count: pending.length },
    { icon: AlertTriangle, color: 'text-red-500 bg-red-50', label: 'Vadesi Geçen', amount: overdueTotal, count: overdue.length },
    { icon: Calendar, color: 'text-blue-500 bg-blue-50', label: 'Bugün Vadeli', amount: todayTotal, count: dueToday.length },
    { icon: TrendingUp, color: 'text-green-500 bg-green-50', label: 'Tahsil Edilen', amount: paidTotal, count: paid.length },
  ];

  const filters = [
    { id: 'all', label: 'Tümünü', count: payments.length },
    { id: 'pending', label: 'Bekleyenler', count: pending.length, icon: Clock },
    { id: 'overdue', label: 'Geciken', count: overdue.length, icon: AlertTriangle },
    { id: 'today', label: 'Bugün', count: dueToday.length, icon: Calendar },
    { id: 'paid', label: 'Tahsil', count: paid.length, icon: TrendingUp },
  ];

  return (
    <div className="space-y-3 sm:space-y-6" data-testid="admin-payment-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ödeme Takibi</h1>
          <p className="text-sm text-gray-500">Ödeme sözlerini takip edin</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setShowDeleted(!showDeleted); if (!showDeleted) fetchDeletedPayments(); }} className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium ${showDeleted ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-testid="deleted-payments-btn">
            <Archive className="w-4 h-4" />Silinenler{deletedPayments.length > 0 && ` (${deletedPayments.length})`}
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50" data-testid="reminder-settings-btn">
            <Settings className="w-4 h-4" /><span className="hidden sm:inline">Hatırlatma Ayarları</span>
          </button>
          <button onClick={openAddModal} className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a]" data-testid="add-payment-btn">
            <Plus className="w-4 h-4" />Ödeme Sözü
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-testid={`stat-card-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 rounded-lg ${s.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium text-gray-500">{s.label}</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(s.amount)}</p>
            <p className="text-xs text-gray-400">{s.count} adet</p>
          </div>
        ))}
      </div>

      {/* Message Status & Auto-Reminder Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gray-100 text-gray-500"><MessageCircle className="w-4 h-4" /></div>
            <span className="text-sm font-medium text-gray-700">Mesaj Durumu</span>
            <span className="text-sm text-gray-500 ml-2">{msgSent} gönderildi</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${waStatus.status === 'connected' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="wa-connection-badge">
              <span className={`w-1.5 h-1.5 rounded-full ${waStatus.status === 'connected' ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              WhatsApp: {waStatus.status === 'connected' ? 'Bağlı' : 'Bağlı Değil'}
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${reminderSettings?.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {reminderSettings?.enabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
              Otomatik Hatırlatma: {reminderSettings?.enabled ? 'Açık' : 'Kapalı'}
            </span>
          </div>
        </div>
        {waStatus.status !== 'connected' && (
          <p className="mt-2 text-[11px] text-[#1a224f] flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0" />
            WhatsApp bağlı değil — yeni mesajlar gönderilemiyor. <a href="/admin/whatsapp" className="underline font-medium ml-1">Bağlantıyı kontrol et</a>
          </p>
        )}
      </div>

      {/* Reminder Settings Panel */}
      {showSettings && reminderSettings && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4" data-testid="reminder-settings-panel">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Settings className="w-4 h-4 text-gray-500" />Otomatik Hatırlatma Ayarları</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Durum</label>
              <button
                onClick={() => setReminderSettings({...reminderSettings, enabled: !reminderSettings.enabled})}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  reminderSettings.enabled ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
                data-testid="toggle-global-reminder"
              >
                {reminderSettings.enabled ? 'Aktif' : 'Pasif'}
              </button>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vadeden Kaç Gün Önce</label>
              <input
                type="number" min="1" max="30"
                value={reminderSettings.days_before_due}
                onChange={(e) => setReminderSettings({...reminderSettings, days_before_due: parseInt(e.target.value) || 3})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="days-before-due"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Gecikme Hatırlatma Aralığı (gün)</label>
              <input
                type="number" min="1" max="30"
                value={reminderSettings.overdue_reminder_interval}
                onChange={(e) => setReminderSettings({...reminderSettings, overdue_reminder_interval: parseInt(e.target.value) || 7})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="overdue-interval"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Gönderim Saati</label>
              <input
                type="number" min="0" max="23"
                value={reminderSettings.reminder_hour}
                onChange={(e) => setReminderSettings({...reminderSettings, reminder_hour: parseInt(e.target.value) || 10})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="reminder-hour"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowSettings(false)} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
            <button onClick={saveReminderSettings} disabled={savingSettings} className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50" data-testid="save-settings-btn">
              {savingSettings ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Kaydet
            </button>
          </div>
        </div>
      )}

      {/* Search & Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Ödeme sözü ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
            data-testid="search-payments"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f.id ? 'bg-[#1a224f] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${f.id}`}
            >
              {f.icon && <f.icon className="w-3 h-3" />}
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      {/* Payment List */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Ödeme Sözü Bulunamadı</h3>
          <p className="text-sm text-gray-500 mb-4">{search ? 'Arama kriterlerinize uygun sonuc yok.' : 'Henuz odeme sozu eklenmemis.'}</p>
          <button onClick={openAddModal} className="inline-flex items-center px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a]">
            <Plus className="w-4 h-4 mr-2" />Ilk Odeme Sozunu Ekle
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table (lg+) */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 px-2 py-2.5 text-left text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Müşteri</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Telefon</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500">Vade</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-32">Durum</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-32">Mesaj Durumu</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-80">Islemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => {
                  const status = getStatusBadge(p);
                  return (
                    <tr key={p.id} className={`border-b border-gray-100 transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`payment-${p.id}`}>
                      <td className="px-2 py-2 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-medium text-gray-900">{p.customer_name}</p>
                          {p.dealer_id && <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-600">Bayi</span>}
                          {(p.dealer_city || p.dealer_district) && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                              <MapPin className="w-2.5 h-2.5" />{[p.dealer_city, p.dealer_district].filter(Boolean).join(' / ')}
                            </span>
                          )}
                        </div>
                        {p.dealer_company && <p className="text-[11px] text-gray-400 truncate max-w-[260px]">{p.dealer_company}</p>}
                        {p.description && <p className="text-[11px] text-gray-400 truncate max-w-[260px]">{p.description}</p>}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{p.customer_phone || '-'}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center gap-1 text-gray-600 text-xs"><Calendar className="w-3 h-3" />{formatDate(p.due_date)}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {(() => {
                          const ms = p.message_status;
                          if (ms === 'sent') {
                            return (
                              <div className="inline-flex flex-col items-center" title={p.message_status_at ? `Gonderildi: ${formatDate(p.message_status_at)}` : 'Gonderildi'}>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700"><Check className="w-3 h-3" />Gonderildi</span>
                                {p.reminder_sent_count > 1 && <span className="text-[9px] text-gray-400 mt-0.5">{p.reminder_sent_count}x</span>}
                              </div>
                            );
                          }
                          if (ms === 'pending') {
                            return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700" title="Mesaj kuyrukta bekliyor"><Loader2 className="w-3 h-3 animate-spin" />Kuyrukta</span>;
                          }
                          if (ms === 'failed') {
                            return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700" title={p.message_status_reason || 'Gonderilemedi'}><X className="w-3 h-3" />Gonderilemedi</span>;
                          }
                          // No message log yet
                          if (p.status === 'paid') {
                            return <span className="text-xs text-gray-400">-</span>;
                          }
                          if (!p.customer_phone) {
                            return <span className="text-xs text-gray-400" title="Telefon yok">Tel. Yok</span>;
                          }
                          return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500" title="Henuz mesaj gonderilmedi">Gonderilmedi</span>;
                        })()}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className="font-semibold text-gray-900 text-xs tabular-nums">{formatCurrency(p.amount)}</span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1 flex-nowrap">
                          {p.status !== 'paid' ? (
                            <button onClick={() => handleMarkPaid(p)} disabled={markingPaid === p.id} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md disabled:opacity-50 transition-colors whitespace-nowrap" data-testid={`mark-paid-${p.id}`}>
                              {markingPaid === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Tahsil Et
                            </button>
                          ) : (
                            <button onClick={() => handleUnmarkPaid(p)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-md transition-colors whitespace-nowrap">
                              <X className="w-3 h-3" />Geri Al
                            </button>
                          )}
                          {p.customer_phone && p.status !== 'paid' && (
                            <button onClick={() => handleSendReminder(p)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors whitespace-nowrap" data-testid={`send-reminder-${p.id}`}>
                              <Send className="w-3 h-3" />Hatirlat
                            </button>
                          )}
                          <button onClick={() => openEditModal(p)} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors whitespace-nowrap">
                            <Edit2 className="w-3 h-3" />Duzenle
                          </button>
                          <button onClick={() => handleDelete(p)} disabled={deleting === p.id} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md disabled:opacity-50 transition-colors whitespace-nowrap" data-testid={`delete-payment-${p.id}`}>
                            {deleting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards (< lg) */}
          <div className="lg:hidden space-y-3">
            {filtered.map(p => {
              const status = getStatusBadge(p);
              const ms = p.message_status;
              return (
                <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-3" data-testid={`payment-card-${p.id}`}>
                  {/* Header: name + amount */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{p.customer_name}</span>
                        {p.dealer_id && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600">Bayi</span>}
                      </div>
                      {(p.dealer_city || p.dealer_district) && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 mt-1 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                          <MapPin className="w-2.5 h-2.5" />{[p.dealer_city, p.dealer_district].filter(Boolean).join(' / ')}
                        </span>
                      )}
                      {p.dealer_company && <p className="text-[11px] text-gray-400 truncate mt-0.5">{p.dealer_company}</p>}
                      {p.customer_phone && <p className="text-[12px] text-gray-500 mt-0.5">{p.customer_phone}</p>}
                      {p.description && <p className="text-[11px] text-gray-400 mt-0.5">{p.description}</p>}
                    </div>
                    <p className="text-base font-bold text-gray-900 shrink-0 tabular-nums">{formatCurrency(p.amount)}</p>
                  </div>

                  {/* Meta: date + status + msg status */}
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="inline-flex items-center gap-1 text-gray-500"><Calendar className="w-3 h-3" />{formatDate(p.due_date)}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${status.color}`}>{status.label}</span>
                    {ms === 'sent' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700"><Check className="w-3 h-3" />Gonderildi{p.reminder_sent_count > 1 && ` ${p.reminder_sent_count}x`}</span>}
                    {ms === 'pending' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700"><Loader2 className="w-3 h-3 animate-spin" />Kuyrukta</span>}
                    {ms === 'failed' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700" title={p.message_status_reason || ''}><X className="w-3 h-3" />Gonderilemedi</span>}
                    {!ms && p.status !== 'paid' && p.customer_phone && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">Gonderilmedi</span>}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100 flex-wrap">
                    {p.status !== 'paid' ? (
                      <button onClick={() => handleMarkPaid(p)} disabled={markingPaid === p.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md disabled:opacity-50 transition-colors flex-1 justify-center min-w-[90px]">
                        {markingPaid === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Tahsil Et
                      </button>
                    ) : (
                      <button onClick={() => handleUnmarkPaid(p)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200 rounded-md transition-colors flex-1 justify-center min-w-[90px]">
                        <X className="w-3 h-3" />Geri Al
                      </button>
                    )}
                    {p.customer_phone && p.status !== 'paid' && (
                      <button onClick={() => handleSendReminder(p)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors flex-1 justify-center min-w-[90px]">
                        <Send className="w-3 h-3" />Hatirlat
                      </button>
                    )}
                    <button onClick={() => openEditModal(p)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors flex-1 justify-center min-w-[80px]">
                      <Edit2 className="w-3 h-3" />Duzenle
                    </button>
                    <button onClick={() => handleDelete(p)} disabled={deleting === p.id} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-md disabled:opacity-50 transition-colors flex-1 justify-center min-w-[60px]">
                      {deleting === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}Sil
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Deleted Payments Modal */}
      {showDeleted && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 pb-20 sm:pb-4 bg-black/50" onClick={() => setShowDeleted(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="deleted-payments-panel">
            <div className="border-b border-gray-100 p-4 flex items-center justify-between bg-white rounded-t-2xl shrink-0">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-gray-500" />
                <h2 className="font-semibold text-gray-900">Silinen Ödeme Sözleri</h2>
                <span className="text-xs text-gray-400">({deletedPayments.length})</span>
              </div>
              <button onClick={() => setShowDeleted(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-500" /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {loadingDeleted ? (
                <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
              ) : deletedPayments.length === 0 ? (
                <div className="py-12 text-center">
                  <Archive className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">Silinmiş ödeme sözü yok</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {deletedPayments.map(p => (
                    <div key={p.id} className="p-3 sm:p-4 flex items-center justify-between gap-3 hover:bg-gray-50/50 transition-colors" data-testid={`deleted-payment-${p.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 text-sm">{p.customer_name}</span>
                          <span className="font-bold text-gray-700 text-sm">{formatCurrency(p.amount)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 flex-wrap">
                          <span>Vade: {p.due_date}</span>
                          <span>|</span>
                          <span className="text-red-500">Silen: {p.deleted_by_name || 'Bilinmiyor'}</span>
                          <span>|</span>
                          <span>{p.deleted_at ? new Date(p.deleted_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                        </div>
                      </div>
                      <button onClick={() => handleRestore(p.id)} disabled={restoring === p.id} className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md disabled:opacity-50" data-testid={`restore-payment-${p.id}`}>
                        {restoring === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Geri Yükle
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-100 p-4 flex items-center justify-between shrink-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">{editingPayment ? 'Ödeme Sözünü Düzenle' : 'Yeni Ödeme Sözü'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Kapat" data-testid="close-payment-modal"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-4 space-y-4" id="payment-form">
              {/* Dealer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bayi Seç</label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={selectedDealer ? `${selectedDealer.company_name || selectedDealer.name}${(selectedDealer.city || selectedDealer.district) ? ` — ${[selectedDealer.city, selectedDealer.district].filter(Boolean).join(' / ')}` : ''}` : dealerSearch}
                      onChange={(e) => {
                        setDealerSearch(e.target.value);
                        if (selectedDealer) {
                          setSelectedDealer(null);
                          setForm(prev => ({ ...prev, dealer_id: '', customer_name: '', customer_phone: '' }));
                        }
                        setShowDealerDropdown(true);
                      }}
                      onFocus={() => setShowDealerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowDealerDropdown(false), 200)}
                      placeholder="Firma, ad, il, ilce veya telefon ara..."
                      className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                      data-testid="payment-dealer-search"
                    />
                    {selectedDealer && (
                      <button type="button" onClick={() => {
                        setSelectedDealer(null);
                        setDealerSearch('');
                        setForm(prev => ({ ...prev, dealer_id: '', customer_name: '', customer_phone: '' }));
                      }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded">
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    )}
                  </div>
                  {showDealerDropdown && !selectedDealer && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {dealers.filter(d => {
                        const q = dealerSearch.toLowerCase();
                        return !q || d.name?.toLowerCase().includes(q) || d.company_name?.toLowerCase().includes(q) || d.phone?.includes(q) || d.city?.toLowerCase().includes(q) || d.district?.toLowerCase().includes(q);
                      }).length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400">Bayi bulunamadı</div>
                      ) : (
                        dealers.filter(d => {
                          const q = dealerSearch.toLowerCase();
                          return !q || d.name?.toLowerCase().includes(q) || d.company_name?.toLowerCase().includes(q) || d.phone?.includes(q) || d.city?.toLowerCase().includes(q) || d.district?.toLowerCase().includes(q);
                        }).slice(0, 50).map(d => (
                          <button key={d.id} type="button" onClick={() => {
                            setSelectedDealer(d);
                            setDealerSearch('');
                            setShowDealerDropdown(false);
                            setForm(prev => ({
                              ...prev,
                              dealer_id: d.id,
                              customer_name: d.name || '',
                              customer_phone: d.phone || '',
                            }));
                          }} className="w-full text-left px-4 py-2.5 hover:bg-[#eef0f7] flex items-center gap-3 border-b border-gray-50 last:border-0" data-testid={`dealer-opt-${d.id}`}>
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-[#1a224f]">{(d.company_name || d.name || 'B').charAt(0)}</span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium text-gray-900 truncate">{d.company_name || d.name}</p>
                                {(d.city || d.district) && (
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                    <MapPin className="w-2.5 h-2.5" />{[d.city, d.district].filter(Boolean).join(' / ')}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 truncate">
                                {d.company_name && d.name && d.name !== d.company_name && <span>{d.name}</span>}
                                {d.phone && <span>{(d.company_name && d.name && d.name !== d.company_name) ? ` · ${d.phone}` : d.phone}</span>}
                              </p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">Bayi seçerek isim ve telefon otomatik doldurulur</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Müşteri Adı <span className="text-red-500">*</span></label>
                <input type="text" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} placeholder="Ad Soyad" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]" required data-testid="payment-customer-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input type="tel" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value})} placeholder="905xxxxxxxxx" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]" data-testid="payment-phone" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tutar (TL) <span className="text-red-500">*</span></label>
                  <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} placeholder="0.00" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]" required data-testid="payment-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vade Tarihi <span className="text-red-500">*</span></label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]" required data-testid="payment-due-date" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Not ekleyin..." rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]" data-testid="payment-description" />
              </div>

              {/* WhatsApp Toggle */}
              {!editingPayment && form.customer_phone && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">WhatsApp Bildirim Gönder</span>
                  </div>
                  <button type="button" onClick={() => setForm({...form, send_whatsapp: !form.send_whatsapp})}
                    className={`relative w-10 h-5 rounded-full transition-colors ${form.send_whatsapp ? 'bg-green-500' : 'bg-gray-300'}`}
                    data-testid="whatsapp-toggle"
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.send_whatsapp ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium" data-testid="cancel-payment-btn">İptal</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center gap-2" data-testid="save-payment-btn">
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</> : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
